<template>
  <div v-if="promoCardImages.length === 1" :data-test-id="dataTestId" :class="outerClass" :style="inlineStyle">
    <template v-if="fields.heading?.value">
      <div class="max-w-full lg:max-w-[936px] text-racq-dark-grey text-base-bold pb-3">
        <RichText :field="fields.heading" />
      </div>
    </template>

    <component
      :is="renderChevronSingle ? 'a' : 'div'"
      :href="renderChevronSingle ? firstPromoCardImage.fields.chevronUrl?.value.href : undefined"
      :target="renderChevronSingle ? firstPromoCardImage.fields.chevronUrl?.value.target : undefined"
      :title="renderChevronSingle ? firstPromoCardImage.fields.chevronUrl?.value.title : undefined"
      :class="[
        'promo-card-image rounded-lg flex-col flex justify-start items-start max-w-full lg:max-w-[936px]',
        {
          'border-pale-blue border-[1.5px] shadow-md': firstPromoCardImage.fields.showBorder.value,
          'no-underline': renderChevronSingle,
        },
        getBackgroundColourClass(firstPromoCardImage.fields.backgroundColour),
      ]"
    >
      <!-- Desktop Horizontal Layout -->
      <div class="hidden md:flex flex-row items-stretch rounded-lg w-full">
        <div
          :class="[
            'flex flex-col justify-center py-6 px-8',
            {
              'w-1/2': firstPromoCardImage.fields.image.value?.src,
              'w-full': !firstPromoCardImage.fields.image.value?.src,
            },
          ]"
        >
          <!-- Tag Component/Tag Text -->
          <template v-if="firstPromoCardImage.fields.tag.value">
            <div class="text-m-bold text-racq-dark-grey pb-2">
              <template v-if="!firstPromoCardImage.fields.tagIsText.value">
                <Tag
                  :tagSize="TagSize.xs"
                  :tagBgColour="TagBgColour.infoBlue"
                  :tagText="firstPromoCardImage.fields.tag.value"
                  :tagType="TagTypes.star"
                />
              </template>

              <template v-if="firstPromoCardImage.fields.tagIsText.value">
                <RichText :field="firstPromoCardImage.fields.tag" />
              </template>
            </div>
          </template>

          <!-- Title -->
          <template v-if="firstPromoCardImage.fields.title.value">
            <div class="text-lg-bold text-racq-dark-grey pb-1">
              <RichText data-test-id="promoTitle" :field="firstPromoCardImage.fields.title" />
            </div>
          </template>

          <!-- Description -->
          <template v-if="firstPromoCardImage.fields.description.value">
            <div class="text-m text-racq-med-grey">
              <RichText data-test-id="promoDescription" :field="firstPromoCardImage.fields.description" />
            </div>
          </template>

          <!-- Buttons/Links -->
          <template
            v-if="
              !firstPromoCardImage.fields.isChevron.value &&
              (isValidPrimaryButton(firstPromoCardImage) || isValidSecondaryButton(firstPromoCardImage))
            "
          >
            <div class="flex flex-wrap mt-4 flex-start gap-4">
              <template v-if="isValidPrimaryButton(firstPromoCardImage)">
                <Link
                  :field="firstPromoCardImage.fields.primaryButton"
                  :class="`flex px-3 py-1.5 justify-center items-center self-stretch text-base btn btn-sm ${firstPromoCardImage.fields.primaryButton?.value.class}`"
                  :aria-label="firstPromoCardImage.fields.primaryButton?.value.text"
                />
              </template>

              <template v-if="isValidSecondaryButton(firstPromoCardImage)">
                <Link
                  :field="firstPromoCardImage.fields.secondaryButton"
                  :class="`flex px-3 py-1.5 justify-center items-center self-stretch text-base btn btn-sm ${firstPromoCardImage.fields.secondaryButton?.value.class}`"
                  :aria-label="firstPromoCardImage.fields.secondaryButton?.value.text"
                />
              </template>
            </div>
          </template>
          <template v-if="renderChevronSingle">
            <div class="flex mt-4">
              <div
                class="w-8 h-8 border-[1.5px] border-racq-light-blue-grey rounded-[40px] inline-flex justify-center items-center"
              >
                <div class="w-2 h-5">
                  <BaseIcon iconClass="bg-icon-chevron" class="!w-4 !h-4" />
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- Image -->
        <template v-if="firstPromoCardImage.fields.image?.value?.src">
          <div class="w-1/2 flex">
            <Image
              :media="firstPromoCardImage.fields.image"
              class="w-full max-w-full min-h-64 h-full object-cover rounded-r-lg"
            />
          </div>
        </template>
      </div>

      <!-- Mobile Vertical Layout -->
      <div class="md:hidden flex flex-col rounded-lg">
        <!-- Image -->
        <template v-if="firstPromoCardImage.fields.image">
          <div>
            <Image
              :media="firstPromoCardImage.fields.image"
              class="w-full max-w-full lg:max-w-[430px] h-auto lg:h-full object-cover rounded-t-lg"
            />
          </div>
        </template>

        <div class="px-4 py-5">
          <!-- Tag Component/Tag Text -->
          <template v-if="firstPromoCardImage.fields.tag">
            <div
              :class="[
                'text-m-bold text-racq-dark-grey pb-2',
                { 'text-center': !firstPromoCardImage.fields.showBorder },
              ]"
            >
              <template v-if="!firstPromoCardImage.fields.tagIsText">
                <Tag
                  :tagSize="TagSize.xs"
                  :tagBgColour="TagBgColour.infoBlue"
                  :tagText="firstPromoCardImage.fields.tag.value"
                  :tagType="TagTypes.star"
                />
              </template>

              <template v-if="firstPromoCardImage.fields.tagIsText.value">
                <RichText :field="firstPromoCardImage.fields.tag" />
              </template>
            </div>
          </template>

          <!-- Title -->
          <template v-if="firstPromoCardImage.fields.title">
            <div
              :class="[
                'text-lg-bold text-racq-dark-grey pb-1',
                { 'text-center': !firstPromoCardImage.fields.showBorder },
              ]"
            >
              <RichText :field="firstPromoCardImage.fields.title" />
            </div>
          </template>

          <!-- Description -->
          <template v-if="firstPromoCardImage.fields.description || renderChevronSingle">
            <div class="flex items-center gap-2">
              <template v-if="firstPromoCardImage.fields.description">
                <div :class="['text-m text-racq-med-grey', { 'text-center': !firstPromoCardImage.fields.showBorder }]">
                  <RichText :field="firstPromoCardImage.fields.description" />
                </div>
              </template>

              <!-- Chevron -->
              <template v-if="firstPromoCardImage.fields.isChevron.value && firstPromoCardImage.fields.chevronUrl">
                <div class="flex mt-4">
                  <Link :field="firstPromoCardImage.fields.chevronUrl">
                    <div
                      class="w-8 h-8 border-[1.5px] border-racq-light-blue-grey rounded-[40px] inline-flex justify-center items-center"
                    >
                      <div class="w-2 h-5">
                        <BaseIcon iconClass="bg-icon-chevron" class="!w-4 !h-4" />
                      </div>
                    </div>
                  </Link>
                </div>
              </template>
            </div>
          </template>

          <!-- Buttons/Links -->
          <template
            v-if="
              !firstPromoCardImage.fields.isChevron.value &&
              (isValidPrimaryButton(firstPromoCardImage) || isValidSecondaryButton(firstPromoCardImage))
            "
          >
            <div :class="['flex mt-4 flex-start gap-4', { 'justify-center': !firstPromoCardImage.fields.showBorder }]">
              <template v-if="isValidPrimaryButton(firstPromoCardImage)">
                <Link
                  :field="firstPromoCardImage.fields.primaryButton"
                  :class="`flex px-3 py-1.5 justify-center items-center self-stretch text-base btn btn-sm ${firstPromoCardImage.fields.primaryButton?.value.class}`"
                  :aria-label="firstPromoCardImage.fields.primaryButton?.value.text"
                />
              </template>

              <template v-if="isValidSecondaryButton(firstPromoCardImage)">
                <Link
                  :field="firstPromoCardImage.fields.secondaryButton"
                  :class="`flex px-3 py-1.5 justify-center items-center self-stretch text-base btn btn-sm ${firstPromoCardImage.fields.secondaryButton?.value.class}`"
                  :aria-label="firstPromoCardImage.fields.secondaryButton?.value.text"
                />
              </template>
            </div>
          </template>
        </div>
      </div>
    </component>
  </div>

  <div v-else :data-test-id="dataTestId">
    <!-- Multiple PromoCardImages - Size will be Medium (for 2) or Small (for 3) -->
    <div class="flex flex-col gap-4">
      <template v-if="fields.heading">
        <div class="max-w-full lg:max-w-[936px] w-full lg:w-[936px] text-racq-dark-grey text-base-bold">
          <RichText :field="fields.heading" />
        </div>
      </template>
      <div class="flex flex-col md:flex-row md:flex-wrap gap-4 w-full">
        <div
          v-for="(promoCardImage, index) in promoCardImages"
          :key="index"
          :data-test-id="promoCardImage.fields.dataTestId?.value || dataTestId + '_' + index"
        >
          <template v-if="promoCardImage.fields.isChevron.value && promoCardImage.fields.chevronUrl">
            <Link
              :field="promoCardImage.fields.chevronUrl"
              class="group block no-underline active:no-underline shadow-md hover:shadow-xl rounded-lg active:rounded-lg active:!shadow-none"
            >
              <PromoCardImageContent
                :promoCardImage="promoCardImage"
                :isWrappedInLink="true"
                :tagSize="TagSize"
                :tagBgColour="TagBgColour"
                :tagTypes="TagTypes"
                :getMaxImageWidthClasses="getMaxImageWidthClasses"
                :getBackgroundColourClass="getBackgroundColourClass"
                :isValidPrimaryButton="isValidPrimaryButton"
                :isValidSecondaryButton="isValidSecondaryButton"
              />
            </Link>
          </template>
          <template v-else>
            <PromoCardImageContent
              :class="{
                'shadow-md': promoCardImage.fields.showBorder,
                'hover:shadow-lg': promoCardImage.fields.showBorder,
              }"
              :promoCardImage="promoCardImage"
              :isWrappedInLink="false"
              :tagSize="TagSize"
              :tagBgColour="TagBgColour"
              :tagTypes="TagTypes"
              :getMaxImageWidthClasses="getMaxImageWidthClasses"
              :getBackgroundColourClass="getBackgroundColourClass"
              :isValidPrimaryButton="isValidPrimaryButton"
              :isValidSecondaryButton="isValidSecondaryButton"
            />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RichText, Link, ComponentFields, Image, ComponentRendering } from '@sitecore-jss/sitecore-jss-vue';
import { BackgroundColours } from '@/interfaces/forms/background-colour.interface';
import { IPromoCardImageItem } from '@/interfaces/entities/promo-card-image.interface';
import Tag from '@/components/Tag/Tag.vue';
import { TagBgColour, TagSize, TagTypes } from '@/enums/tag-enums';
import BaseIcon from '@/components/BaseIcon/BaseIcon.vue';
import PromoCardImageContent from '@/components/PromoCardImage/PromoCardImageContent.vue';
import { usePageStore } from '@/store';

// ----------------------------------------------------------------------------------
// Define variables
// ----------------------------------------------------------------------------------
const props = defineProps<{
  fields: ComponentFields;
  rendering?: ComponentRendering;
}>();

const outerClass = props.rendering?.params?.Styles;
const inlineStyle = props.rendering?.params?.inlineStyle;
const promoCardImages = computed(() => props.fields.items as IPromoCardImageItem[]);
const firstPromoCardImage = computed(() => promoCardImages.value[0] as IPromoCardImageItem);
const renderChevronSingle = computed(
  () => firstPromoCardImage.value.fields.isChevron.value && firstPromoCardImage.value.fields.chevronUrl
);

const dataTestId = usePageStore().dataTestId(props.rendering ?? firstPromoCardImage.value);

function isValidPrimaryButton(promoCardImage: IPromoCardImageItem) {
  if (promoCardImage.fields.primaryButton?.value.text && promoCardImage.fields.primaryButton) {
    return true;
  }
  return false;
}

function isValidSecondaryButton(promoCardImage: IPromoCardImageItem) {
  if (promoCardImage.fields.secondaryButton?.value.text && promoCardImage.fields.secondaryButton) {
    return true;
  }
  return false;
}

function getMaxImageWidthClasses() {
  const promoCards = props.fields.items as IPromoCardImageItem[];
  if (promoCards.length === 1) {
    // return lg / default
    return '';
  } else if (promoCards.length === 2) {
    // return md
    return 'w-[300px] md:w-[340px]';
  } else {
    // return sm
    return 'md:w-[288px]';
  }
}

function getBackgroundColourClass(backgroundColour: any) {
  if (!backgroundColour) {
    return '';
  }
  switch (backgroundColour.value) {
    case BackgroundColours.WHITE:
      return 'bg-racq-true-white';
    case BackgroundColours.LIGHTGREY:
      return 'bg-racq-neutral-grey';
    case BackgroundColours.GREY:
      return 'bg-racq-anti-flash-white';
    default:
      return '';
  }
}
</script>
